import {
  type ComponentType,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactSelect, {
  type ActionMeta,
  components,
  type ControlProps,
  type GroupBase,
  type InputProps,
  type MultiValueProps,
  type OnChangeValue,
  type StylesConfig,
} from 'react-select';

import { SelectInput } from './SelectInput';
import { SelectMultiValueChip } from './SelectMultiValueChip';
import { getSelectClassNames, getSelectStyles } from './selectTheme';
import { type GroupedOption, type Option, type SelectProps } from './types';

const hideComponent = () => null;

// Always return the value as an array.
function ensureArray<TValue>(
  value: TValue | readonly TValue[] | null | undefined,
): TValue[] {
  if (value == null) {
    return [];
  }

  return Array.isArray(value)
    ? Array.from(value as readonly TValue[])
    : [value as TValue];
}

function isGroupedOption<TValue, TData>(
  option: Option<TValue, TData> | GroupedOption<TValue, TData>,
): option is GroupedOption<TValue, TData> {
  return 'options' in option;
}

const flattenOptions = <TValue, TData>(
  options: Array<Option<TValue, TData> | GroupedOption<TValue, TData>>,
) =>
  options.flatMap((option) =>
    isGroupedOption(option) ? option.options : option,
  );

/**
 * A select component for working with client-side data based on the react-select library.
 *
 * @see https://react-select.com/props
 */
export const Select = <T,>({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  className,
  classNames,
  errors,
  filterOption,
  formatOptionLabel,
  inputId,
  isClearable = true,
  isSearchable = true,
  isDisabled = false,
  isItemSelectionAllowed = () => true,
  isLoading = false,
  isMultiSelect = false,
  isOptionDisabled,
  menuPlacement = 'auto',
  isRemovableValue,
  onChange,
  onMultiValueClick,
  options,
  placeholder = 'Element auswählen',
  style,
  styles,
  testId,
  useFullWidthChips = false,
  value,
}: SelectProps<T>) => {
  const [selectedItems, setSelectedItems] = useState<Array<Option<string, T>>>(
    [],
  );

  useEffect(() => {
    if (!options) {
      setSelectedItems([]);
      return;
    }

    // Flatten option groups if present
    const flatOptions = flattenOptions(options);

    if (isMultiSelect) {
      const values = ensureArray(value);
      const selectedOptions = values.flatMap((value_) => {
        const option = flatOptions.find(({ value }) => value === value_);
        return option ? [option] : [];
      });

      setSelectedItems(selectedOptions);
    } else {
      const selectedOption = flatOptions.find(
        (option) => option.value === value,
      );

      setSelectedItems(selectedOption ? [selectedOption] : []);
    }
  }, [value, options, isMultiSelect]);

  const handleChange = useCallback(
    (
      newValue: OnChangeValue<Option<string, T>, boolean>,
      actionMeta: ActionMeta<Option<string, T>>,
    ) => {
      const newSelectedItems = ensureArray(newValue);
      const addedOption =
        actionMeta.action === 'select-option' ||
        actionMeta.action === 'create-option'
          ? actionMeta.option
          : undefined;

      if (addedOption && isOptionDisabled?.(addedOption)) {
        return;
      }

      setSelectedItems(newSelectedItems);

      const newValues = newSelectedItems.map(({ value }) => value);
      const newData = newSelectedItems.map(({ data }) => data);

      onChange(
        isMultiSelect ? newValues : (newValues[0] ?? ''),
        isMultiSelect ? (newData as T[]) : (newData[0] as T),
        actionMeta,
      );
    },
    [onChange, isMultiSelect, isOptionDisabled],
  );

  const Control = useMemo(
    () =>
      memo(
        ({
          children,
          innerProps,
          ...rest
        }: ControlProps<
          Option<string, T>,
          boolean,
          GroupBase<Option<string, T>>
        >) => (
          <components.Control
            {...rest}
            innerProps={
              {
                ...innerProps,
                'data-testid': testId,
              } as typeof innerProps
            }
          >
            {children}
          </components.Control>
        ),
      ),
    [testId],
  );

  const MultiValue = useMemo(
    () =>
      memo(
        ({
          data,
        }: MultiValueProps<
          Option<string, T>,
          boolean,
          GroupBase<Option<string, T>>
        >) => (
          <SelectMultiValueChip
            data={data}
            isRemovable={!isRemovableValue || isRemovableValue(data)}
            useFullWidth={useFullWidthChips}
            onClick={onMultiValueClick}
            onDelete={() => {
              handleChange(
                selectedItems.filter(({ value }) => value !== data.value),
                { action: 'remove-value', removedValue: data },
              );
            }}
          />
        ),
      ),
    [
      useFullWidthChips,
      onMultiValueClick,
      handleChange,
      selectedItems,
      isRemovableValue,
    ],
  );

  const components_ = useMemo(
    () => ({
      Control,
      IndicatorSeparator: hideComponent,
      Input: SelectInput as ComponentType<
        InputProps<Option<string, T>, boolean, GroupBase<Option<string, T>>>
      >,
      MultiValue,
    }),
    [Control, MultiValue],
  );

  const memoizedStyles = useMemo(
    () =>
      getSelectStyles({
        ...styles,
        ...(style as StylesConfig<
          Option<string, T>,
          boolean,
          GroupBase<Option<string, T>>
        >),
      }),
    [style, styles],
  );

  const selectedValue = useMemo(
    () => (isMultiSelect ? selectedItems : (selectedItems[0] ?? null)),
    [isMultiSelect, selectedItems],
  );

  const hasErrors = Boolean(errors);

  const memoizedClassNames = useMemo(
    () => getSelectClassNames({ classNames, hasErrors }),
    [hasErrors, classNames],
  );

  return (
    <ReactSelect<Option<string, T>, boolean, GroupBase<Option<string, T>>>
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      className={className}
      classNames={memoizedClassNames}
      components={components_}
      {...(filterOption === undefined ? {} : { filterOption })}
      {...(isOptionDisabled === undefined ? {} : { isOptionDisabled })}
      formatOptionLabel={formatOptionLabel}
      inputId={inputId}
      isClearable={isClearable}
      isSearchable={isSearchable}
      isDisabled={isDisabled || !isItemSelectionAllowed()}
      isLoading={isLoading}
      isMulti={isMultiSelect}
      menuPlacement={menuPlacement}
      menuPortalTarget={document.body}
      noOptionsMessage={() => 'Keine passenden Daten gefunden'}
      options={options}
      placeholder={placeholder}
      styles={memoizedStyles}
      value={selectedValue}
      onChange={handleChange}
    />
  );
};
